import { normalizeEmailLinkUrl } from "./emailSafeLink";

// Distinguish editor HTML from legacy plain text, which can contain literal tags.
// Existing draft values are not migrated merely by opening the editor.
export const emailRichTextPrefix = "<!--cmp-rich-text-->";
export const isEmailRichText = (value: string): boolean =>
  value.startsWith(emailRichTextPrefix);

const allowedTags = new Set(["p", "div", "br", "b", "strong", "i", "em", "u", "span", "a", "ul", "ol", "li"]);
const discardedTags = new Set(["script", "style", "iframe", "object", "svg", "math", "template"]);

/** Rebuild editor HTML from an inert tree. Never copy arbitrary attributes/CSS. */
export const sanitizeEmailRichText = (html: string): string => {
  const template = document.createElement("template");
  template.innerHTML = html;
  const output = document.createElement("div");
  const append = (node: Node, parent: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode((node.textContent || "").replace(/\u00a0/g, " ")));
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    const tag = node.tagName.toLowerCase();
    if (discardedTags.has(tag)) return;
    if (!allowedTags.has(tag)) {
      node.childNodes.forEach((child) => append(child, parent));
      return;
    }
    const clean = document.createElement(tag);
    const size = node.style.fontSize;
    // Preserve existing custom pixel sizes as well as the visible design choices.
    if (/^\d+(?:\.\d+)?px$/.test(size) && Number.parseFloat(size) > 0 && Number.parseFloat(size) <= 512) {
      clean.style.fontSize = size;
    }
    const alignment = node.style.textAlign || Array.from(node.classList).find((name) => /^ql-align-(left|center|right|justify)$/.test(name))?.slice(9);
    if (alignment && /^(left|center|right|justify)$/.test(alignment)) {
      clean.style.textAlign = alignment;
      if (tag === "span") clean.style.display = "block";
    }
    if (/^(bold|[6-9]00)$/.test(node.style.fontWeight)) clean.style.fontWeight = node.style.fontWeight;
    if (node.style.fontStyle === "italic") clean.style.fontStyle = "italic";
    if (node.style.textDecoration === "underline") clean.style.textDecoration = "underline";
    if (tag === "p" || tag === "div") clean.style.margin = "0";
    if (tag === "ul" || tag === "ol") {
      clean.style.margin = "0";
      clean.style.paddingLeft = "24px";
      clean.style.listStyleType = tag === "ul" ? "disc" : "decimal";
    }
    if (tag === "a") {
      const href = normalizeEmailLinkUrl(node.getAttribute("href") || "");
      if (href) {
        clean.setAttribute("href", href);
        clean.style.textDecoration = "underline";
      }
    }
    node.childNodes.forEach((child) => append(child, clean));
    // The editor saves a blank line as an empty paragraph, which has no height
    // in the preview or the email. A line break keeps the blank line visible.
    if ((tag === "p" || tag === "div") && !clean.textContent?.trim() && !clean.querySelector("br")) {
      clean.appendChild(document.createElement("br"));
    }
    parent.appendChild(clean);
  };
  template.content.childNodes.forEach((node) => append(node, output));
  return output.innerHTML;
};

export const buildEmailRichTextValue = (html: string): string =>
  emailRichTextPrefix + sanitizeEmailRichText(html);

export const emailRichTextHtml = (value: string): string =>
  sanitizeEmailRichText(value.slice(emailRichTextPrefix.length));

export const emailRichTextToPlainText = (value: string): string => {
  const template = document.createElement("template");
  template.innerHTML = emailRichTextHtml(value);
  template.content.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  template.content.querySelectorAll("p,div,li").forEach((element) => element.append("\n"));
  return template.content.textContent?.trim() || "";
};
